/**
 * shell/Announcer.tsx —— `aria-live` **只播终态**（壳规范 §1bis·五；判据 `ui_a11y_baseline`）。
 *
 * 规范逐字：`aria-live` **只播终态**（**流式增量不逐个播报**）。
 * 实装口径：状态机里唯「终态」可播——`open`（连接成功）／`closed`（已断开）／`error`（失败）；
 *          `connecting`／`reconnecting`／`idle` 属**中间态**，**不播**（避免刷屏）。
 */
import React from "react";

export type TransportStatusLike = "idle" | "connecting" | "open" | "reconnecting" | "closed";

/** 可播（终态）集合：增量/中间态不入 */
export const TERMINAL_STATUSES: readonly TransportStatusLike[] = ["open", "closed"];

export function isTerminalStatus(status: string): boolean {
  return (TERMINAL_STATUSES as readonly string[]).includes(status);
}

export interface AnnouncerProps {
  message: string;
  /** 断言用：终态才为真（非终态时本件不渲染文本） */
  live?: "polite" | "assertive";
}

/** 终态播报区（流式增量不进入本区） */
export function Announcer({ message, live = "polite" }: AnnouncerProps): React.ReactElement {
  return (
    <div className="xg-sr-only" role="status" aria-live={live} aria-atomic="true" data-testid="announcer">
      {message}
    </div>
  );
}

/** 终态播报状态机：只接受终态字符串（非终态 ⇒ 空串，不播） */
export function useTerminalAnnouncement(): {
  message: string;
  announce: (text: string, terminal: boolean) => void;
} {
  const [message, setMessage] = React.useState("");
  const announce = React.useCallback((text: string, terminal: boolean) => {
    if (!terminal) return;                 // 中间态/流式增量：不播
    setMessage(text);
  }, []);
  return { message, announce };
}
